import {
    NO,
    UIColor,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    UIViewController,
    YES
} from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import {
    CBDataViewCellDescriptor,
    CBDataViewCellDescriptorWithObject
} from "../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHCheckbox } from "../Custom components/RH/RHCheckbox"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { RHCPVCodeFormatter } from "../Custom components/RH/Utils_slop/RHCPVCodeFormatter"
import {
    RHButtons,
    RHColors,
    RHFont,
    RHSpacing
} from "../Custom components/RH/Utils_slop/RHDesignTokens"
import {
    RHCompanyTenderInterestViewTokens,
    RHTableColumnMinimumWidths
} from "../Custom components/RH/Utils_slop/RHViewTokens"
import { CPVTreeDialogView } from "../NewProcurement/CPVTreeDialogView"
import { RHOffererTenderInterestSettings } from "../SocketClientFunctions"


interface TenderInterestRow {
    cpvCode: string
    cpvText: string
    canRemove: boolean
}


const administratorTenderInterestExplanation =
    "Siin haldad ettevõtte ühist CPV-valikut. See määrab, milliste tegevusvaldkondade puhul võib " +
    "ettevõte ilmuda hankijale võimalike pakkujate soovituste hulka. Sama valikut kasutatakse kõigi " +
    "peakasutajate ning nende ettevõtte esindajate filtris „Mind huvitavad hanked“, kes on valinud " +
    "ettevõtte CPV-valiku arvestamise."


const administratorTenderInterestConsequences =
    "Ettevõtte valik koosneb keskkonna vaikekoodidest ja peakasutajate lisatud koodidest. Saad lisada " +
    "ettevõtte tegevusele sobivaid koode ning eemaldada varem lisatud koode. Keskkonna vaikekoode ei " +
    "saa eemaldada ega välja lülitada – need jäävad alati ettevõtte valikusse. Kui oled RHR-i viimase " +
    "90 päeva jooksul kasutanud, annab rakendus uuest valikule vastavast avalikust hankest märku. " +
    "CPV-põhist märguannet e-postiga ei saadeta."


const companyRepresentativeTenderInterestExplanation =
    "Siin saad määrata, milliste tegevusvaldkondade avalikke hankeid kuvatakse sulle filtris „Mind " +
    "huvitavad hanked“. Kui oled RHR-i viimase 90 päeva jooksul kasutanud, annab rakendus uuest sinu " +
    "valikule vastavast avalikust hankest märku. CPV-põhist märguannet e-postiga ei saadeta."


const companyRepresentativeTenderInterestConsequences =
    "Vaikimisi arvestatakse sinu puhul nii ettevõtte CPV-valikut kui ka sinu lisatud koode. Ettevõtte " +
    "valikut saab muuta ainult peakasutaja, kuid sina saad lisada ja eemaldada enda koode ning ettevõtte " +
    "valiku arvestamise välja lülitada. Sinu isiklik valik ei mõjuta seda, milliste tegevusvaldkondade " +
    "puhul ettevõtet hankijale võimaliku pakkujana soovitatakse."


const companyCpvSelectionActionExplanation =
    "Nupp „Täienda ettevõtte CPV-valikut“ avab CPV-koodide valiku. Lisa ettevõttele sobivaid koode " +
    "või eemalda valikust varem lisatud koode. Keskkonna vaikekoodid on seal juba valitud ja neid " +
    "ei saa eemaldada. Üldisema koodi valimisel valitakse ka selle alamkoodid; üldisema koodi valiku " +
    "eemaldamisel eemaldatakse ka alamkoodide valik."


const companyCpvSelectionActionOutcome =
    "„Salvesta muudatused“ salvestab uue ettevõtte CPV-valiku. Sellega määrad tegevusvaldkonnad, " +
    "mille puhul võib ettevõte ilmuda hankijale osalema kutsutavate pakkujate soovitustesse. See ei " +
    "saada ettevõttele automaatselt kutset – kutsutavad ettevõtted valib hankija. Valikut " +
    "arvestatakse ka ettevõtte CPV-valikut kasutavate inimeste filtris „Mind huvitavad hanked“ ning " +
    "uute sobivate avalike hangete rakendusesisestes märguannetes. CPV-põhist e-kirja ei saadeta."


const personalCpvSelectionActionExplanation =
    "Nupp „Lisa täiendavaid CPV-koode“ või „Muuda täiendavaid CPV-koode“ avab CPV-puu. Vali koodid, " +
    "mida soovid arvestada enda huvide filtris ja märguannetes."


const personalCpvSelectionActionOutcome =
    "Üldisema koodi valimisel valitakse ka selle alamkoodid; üldisema koodi valiku eemaldamisel " +
    "eemaldatakse ka alamkoodide valik. „Salvesta valik“ salvestab valiku ainult sulle; see ei " +
    "muuda ettevõtte CPV-valikut ega ettevõtte soovitamist hankijale."


class TenderInterestGuidanceView extends UIView {

    readonly _titleLabel = new UITextView().configuredWithObject({
        textColor: RHColors.textHeading,
        isSingleLine: NO,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeMedium,
            fontWeight: RHFont.weightSemibold,
            lineHeight: RHFont.lineHeightBase
        }
    }).addedAsSubviewToView(this)

    readonly _bodyLabel = new UITextView().configuredWithObject({
        textColor: RHColors.textSecondary,
        isSingleLine: NO,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeMedium,
            fontWeight: RHFont.weightRegular,
            lineHeight: RHFont.lineHeightBase
        }
    }).addedAsSubviewToView(this)

    readonly _secondaryBodyLabel = new UITextView().configuredWithObject({
        textColor: RHColors.textSecondary,
        isSingleLine: NO,
        hidden: YES,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeMedium,
            fontWeight: RHFont.weightRegular,
            lineHeight: RHFont.lineHeightBase
        }
    }).addedAsSubviewToView(this)

    constructor(
        title: string,
        body: string,
        secondaryBody: string = ""
    ) {
        super()
        this.configureContent(title, body, secondaryBody)
    }


    configureContent(
        title: string,
        body: string,
        secondaryBody: string = ""
    ) {
        this._titleLabel.text = title
        this._bodyLabel.text = body
        this._secondaryBodyLabel.text = secondaryBody
        this._secondaryBodyLabel.hidden = YES
        if (secondaryBody.length > 0) {
            this._secondaryBodyLabel.hidden = NO
        }
        this.setNeedsLayout()
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        bounds
            .rectangleWithHeight(this._titleLabel)
            .assignedAsFrameOfView(this._titleLabel)
            .rectangleForNextRow(RHSpacing.tiny, this._bodyLabel)
            .assignedAsFrameOfView(this._bodyLabel)
            .IF(this._secondaryBodyLabel.hidden === NO)
            .rectangleForNextRow(RHSpacing.small)
            .rectangleWithHeight(this._secondaryBodyLabel)
            .assignedAsFrameOfView(this._secondaryBodyLabel)
            .ELSE()
            .ENDIF()
    }


    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        const textWidth = Math.max(1, constrainingWidth)
        let contentHeight = this._titleLabel.intrinsicContentHeight(textWidth) +
            RHSpacing.tiny +
            this._bodyLabel.intrinsicContentHeight(textWidth)
        if (this._secondaryBodyLabel.hidden === NO) {
            contentHeight += RHSpacing.small +
                this._secondaryBodyLabel.intrinsicContentHeight(textWidth)
        }
        return contentHeight
    }

}


class TenderInterestActionRowView extends UIView {

    readonly defaultSelectionCheckbox: RHCheckbox
    readonly companyPrimaryButton: RHButton
    readonly personalPrimaryButton: RHButton
    _primaryButton: RHButton


    constructor(
        defaultSelectionCheckbox: RHCheckbox,
        companyPrimaryButton: RHButton,
        personalPrimaryButton: RHButton
    ) {
        super()
        this.defaultSelectionCheckbox = defaultSelectionCheckbox.addedAsSubviewToView(this)
        this.companyPrimaryButton = companyPrimaryButton.addedAsSubviewToView(this)
        this.personalPrimaryButton = personalPrimaryButton.addedAsSubviewToView(this)
        this._primaryButton = companyPrimaryButton
        this.configureForCompanyAdministrator(YES)
    }


    configureForCompanyAdministrator(isCompanyAdministrator: boolean) {
        this._primaryButton = this.personalPrimaryButton
        this.companyPrimaryButton.hidden = NO
        this.personalPrimaryButton.hidden = YES
        if (!isCompanyAdministrator) {
            this.companyPrimaryButton.hidden = YES
            this.personalPrimaryButton.hidden = NO
        }
        else {
            this._primaryButton = this.companyPrimaryButton
        }
        this.setNeedsLayout()
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        const checkboxWidth =
            this.defaultSelectionCheckbox.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHSpacing.xLarge +
            RHSpacing.small
        const primaryButtonWidth = this._buttonWidth(this._primaryButton)
        const usesStackedLayout = this._usesStackedLayoutForWidth(bounds.width)

        bounds
            .IF(usesStackedLayout)
            .rectangleWithHeight(RHButtons.compactHeight)
            .rectangleWithWidth([checkboxWidth, bounds.width].min())
            .assignedAsFrameOfView(this.defaultSelectionCheckbox)
            .TRANSFORM(() => bounds)
            .rectangleWithHeight(RHButtons.compactHeight)
            .rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
            .rectangleWithWidth(primaryButtonWidth, 0.5)
            .assignedAsFrameOfView(this._primaryButton)
            .ELSE()
            .rectangleWithHeight(RHButtons.compactHeight, 0.5)
            .rectangleWithWidth(checkboxWidth)
            .assignedAsFrameOfView(this.defaultSelectionCheckbox)
            .TRANSFORM(() => bounds)
            .rectangleWithHeight(RHButtons.compactHeight, 0.5)
            .rectangleWithWidth(primaryButtonWidth, 0.5)
            .assignedAsFrameOfView(this._primaryButton)
            .ENDIF()
    }


    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        let contentHeight = RHButtons.compactHeight
        if (this._usesStackedLayoutForWidth(constrainingWidth)) {
            contentHeight += RHSpacing.small + RHButtons.compactHeight
        }
        return contentHeight
    }


    _usesStackedLayoutForWidth(constrainingWidth: number): boolean {
        const checkboxWidth =
            this.defaultSelectionCheckbox.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHSpacing.xLarge +
            RHSpacing.small
        const primaryButtonWidth = this._buttonWidth(this._primaryButton)
        return checkboxWidth + RHSpacing.small >
            (constrainingWidth - primaryButtonWidth) * 0.5
    }


    _buttonWidth(button: RHButton): number {
        return button.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHButtons.compactContentPadding * 2 +
            RHSpacing.small
    }

}


class TenderInterestDataView extends RHDataView<TenderInterestRow> {

    readonly headerActionButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Kustuta kõik",
                textAlignment: UITextView.textAlignment.center,
                style: {
                    whiteSpace: "nowrap",
                    fontWeight: RHCompanyTenderInterestViewTokens.headerAction.fontWeight
                }
            },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHCompanyTenderInterestViewTokens.headerAction.contentPadding,
            style: {
                fontSize: RHCompanyTenderInterestViewTokens.headerAction.fontSize
            },
            hidden: YES,
            enabled: NO
        })
        .addedAsSubviewToView(this)


    override layoutSubviews() {
        super.layoutSubviews()
        if (this.headerActionButton.hidden) {
            return
        }

        const titleFrame = this.titleLabel.frame
        const tableAlignedTitleFrame = titleFrame.rectangleWithInsets(
            0,
            this.tableHeaderView.sideCellsWidth,
            0,
            0
        )
        const headerActionButtonWidth = [
            this.headerActionButton.titleLabel.intrinsicContentWidth(
                RHCompanyTenderInterestViewTokens.headerAction.height
            ) +
            RHCompanyTenderInterestViewTokens.headerAction.contentPadding * 2 +
            RHCompanyTenderInterestViewTokens.headerAction.widthSafetyBuffer,
            RHCompanyTenderInterestViewTokens.headerAction.minimumWidth
        ].max()

        this.headerActionButton.frame = tableAlignedTitleFrame
            .rectangleWithWidth(headerActionButtonWidth, 1)
            .rectangleWithHeight(
                RHCompanyTenderInterestViewTokens.headerAction.height,
                0.5
            )
        this.titleLabel.frame = titleFrame.rectangleWithWidth([
            0,
            tableAlignedTitleFrame.width -
            headerActionButtonWidth -
            RHCompanyTenderInterestViewTokens.headerAction.gap
        ].max())
    }

}


export class CompanyTenderInterestViewController extends UIViewController {
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Ettevõtte leitavus ja hanketeavitused",
        backgroundColor: UIColor.transparentColor,
        textColor: RHColors.textPrimary
    }).addedAsSubviewToView(this.view)
    
    readonly roleGuidanceView = new TenderInterestGuidanceView(
        "Pakkuja ettevõtte esindaja võimalused",
        companyRepresentativeTenderInterestExplanation,
        companyRepresentativeTenderInterestConsequences
    )
        .addedAsSubviewToView(this.view)

    readonly cpvSelectionGuidanceView = new TenderInterestGuidanceView(
        "Täiendavate CPV-koodide muutmine",
        personalCpvSelectionActionExplanation,
        personalCpvSelectionActionOutcome
    )
        .addedAsSubviewToView(this.view)
    
    readonly editCompanyTenderInterestButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Täienda ettevõtte CPV-valikut" },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: { fontSize: RHButtons.compactFontSize },
            enabled: NO,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._showCompanyTenderInterestDialog()
                }
            }
        })

    readonly _companyTenderInterestCodeDescriptor =
        CBDataViewCellDescriptorWithObject<TenderInterestRow>({
            title: "CPV-kood",
            keyPath: "cpvCode",
            weight: RHCompanyTenderInterestViewTokens.columnWeights.cpvCode,
            allowSorting: YES
        })

    readonly _companyTenderInterestDescriptionDescriptor =
        CBDataViewCellDescriptorWithObject<TenderInterestRow>({
            title: "Kirjeldus",
            keyPath: "cpvText",
            weight: RHCompanyTenderInterestViewTokens.columnWeights.description,
            allowSorting: YES
        })

    readonly _companyTenderInterestActionDescriptor:
        CBDataViewCellDescriptor<TenderInterestRow, RHTableActionButtonView> =
        CBDataViewCellDescriptorWithObject<TenderInterestRow, RHTableActionButtonView>({
            title: "",
            keyPath: "",
            allowSorting: NO,
            hidden: YES,
            weight: RHCompanyTenderInterestViewTokens.columnWeights.action,
            minimumWidth: RHTableColumnMinimumWidths.tableAction,
            initCustomView: () => new RHTableActionButtonView(),
            updateCell: (cellView, dataItem) => {
                cellView.customView.text = "Eemalda"
                cellView.customView.button.variant = RHButtons.destructiveUtilityVariant
                cellView.customView.hidden =
                    !this._canEditCompanyDefault || !dataItem.canRemove
                cellView.customView.buttonWasPressed = () => {
                    this._removeCompanyTenderInterestCode(dataItem.cpvCode)
                }
            }
        }) as CBDataViewCellDescriptor<TenderInterestRow, RHTableActionButtonView>

    readonly _companyTenderInterestDescriptors = [
        this._companyTenderInterestCodeDescriptor,
        this._companyTenderInterestDescriptionDescriptor,
        this._companyTenderInterestActionDescriptor
    ]
    
    readonly companyTenderInterestDataView = new TenderInterestDataView()
        .configuredWithObject({
            titleLabel: { text: "Ettevõtte CPV-valik" },
            isSearchAvailable: NO,
            noDataMessage: "Ettevõtte CPV-valik on tühi.",
            descriptors: this._companyTenderInterestDescriptors
        })
        .addedAsSubviewToView(this.view)

    readonly clearCompanyTenderInterestButton =
        this.companyTenderInterestDataView.headerActionButton
            .configuredWithObject({
                titleLabel: { text: "Kustuta lisatud koodid" },
                controlEventTargetAccumulator: {
                    PrimaryActionTriggered: () => {
                        this._confirmAndClearAllCompanyTenderInterestCodes()
                    }
                }
            })

    readonly defaultSelectionCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: { text: "Arvesta ettevõtte CPV-valikut" },
            selected: YES,
            enabled: NO,
            hidden: YES,
            controlEventTargetAccumulator: {
                SelectionChange: () => {
                    this._defaultSelectionDidChange()
                }
            }
        })

    readonly editPersonalTenderInterestButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Lisa täiendavaid CPV-koode" },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: { fontSize: RHButtons.compactFontSize },
            enabled: NO,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._showPersonalTenderInterestDialog()
                }
            }
        })

    readonly personalTenderInterestDataView = new TenderInterestDataView()
        .configuredWithObject({
            titleLabel: { text: "Minu täiendavad CPV-koodid" },
            isSearchAvailable: NO,
            noDataMessage: "Täiendavaid CPV-koode pole valitud.",
            hidden: YES,
            descriptors: [
                CBDataViewCellDescriptorWithObject<TenderInterestRow>({
                    title: "CPV-kood",
                    keyPath: "cpvCode",
                    weight: RHCompanyTenderInterestViewTokens.columnWeights.cpvCode,
                    allowSorting: YES
                }),
                CBDataViewCellDescriptorWithObject<TenderInterestRow>({
                    title: "Kirjeldus",
                    keyPath: "cpvText",
                    weight: RHCompanyTenderInterestViewTokens.columnWeights.description,
                    allowSorting: YES
                }),
                CBDataViewCellDescriptorWithObject<TenderInterestRow, RHTableActionButtonView>({
                    title: "",
                    keyPath: "",
                    allowSorting: NO,
                    weight: RHCompanyTenderInterestViewTokens.columnWeights.action,
                    minimumWidth: RHTableColumnMinimumWidths.tableAction,
                    initCustomView: () => new RHTableActionButtonView(),
                    updateCell: (cellView, dataItem) => {
                        cellView.customView.text = "Eemalda"
                        cellView.customView.button.variant = RHButtons.destructiveUtilityVariant
                        cellView.customView.hidden = NO
                        cellView.customView.buttonWasPressed = () => {
                            this._removePersonalTenderInterestCode(dataItem.cpvCode)
                        }
                    }
                })
            ]
        })
        .addedAsSubviewToView(this.view)

    readonly clearPersonalTenderInterestButton =
        this.personalTenderInterestDataView.headerActionButton
            .configuredWithObject({
                controlEventTargetAccumulator: {
                    PrimaryActionTriggered: () => {
                        this._confirmAndClearAllPersonalTenderInterestCodes()
                    }
                }
            })

    readonly tenderInterestActionRow = new TenderInterestActionRowView(
        this.defaultSelectionCheckbox,
        this.editCompanyTenderInterestButton,
        this.editPersonalTenderInterestButton
    )
        .configuredWithObject({ hidden: YES })
        .addedAsSubviewToView(this.view)
    
    readonly cpvTreeDialog = new CPVTreeDialogView()
        .configuredWithObject({ confirmsSelectionOnDismiss: NO })
    
    override _focusManager = new RHFocusManager(() => {
        const focusableItems: UIView[] = []
        if (this._canEditCompanyDefault) {
            if (
                this.defaultSelectionCheckbox.enabled &&
                !this.defaultSelectionCheckbox.hidden
            ) {
                focusableItems.push(this.defaultSelectionCheckbox)
            }
            if (
                this.editCompanyTenderInterestButton.enabled &&
                this.editCompanyTenderInterestButton.isMemberOfViewTree
            ) {
                focusableItems.push(this.editCompanyTenderInterestButton)
            }
            if (
                this.clearCompanyTenderInterestButton.enabled &&
                this.clearCompanyTenderInterestButton.isMemberOfViewTree
            ) {
                focusableItems.push(this.clearCompanyTenderInterestButton)
            }
            if (!this.companyTenderInterestDataView.hidden) {
                focusableItems.push(this.companyTenderInterestDataView._gridContainerView)
            }
            return focusableItems
        }

        if (!this.companyTenderInterestDataView.hidden) {
            focusableItems.push(this.companyTenderInterestDataView._gridContainerView)
        }
        if (
            this.defaultSelectionCheckbox.enabled &&
            !this.defaultSelectionCheckbox.hidden
        ) {
            focusableItems.push(this.defaultSelectionCheckbox)
        }
        if (
            this.editPersonalTenderInterestButton.enabled &&
            this.editPersonalTenderInterestButton.isMemberOfViewTree
        ) {
            focusableItems.push(this.editPersonalTenderInterestButton)
        }
        if (
            this.clearPersonalTenderInterestButton.enabled &&
            this.clearPersonalTenderInterestButton.isMemberOfViewTree
        ) {
            focusableItems.push(this.clearPersonalTenderInterestButton)
        }
        if (!this.personalTenderInterestDataView.hidden) {
            focusableItems.push(this.personalTenderInterestDataView._gridContainerView)
        }
        return focusableItems
    })
    
    _companyTenderInterestCodes: string[] = []
    _companyDefaultTenderInterestCodes: string[] = []
    _companyAdditionalTenderInterestCodes: string[] = []
    _personalTenderInterestCodes: string[] = []
    _companyUsesSystemDefault = YES
    _personalIncludesCompanySelection = YES
    _canEditCompanyDefault = NO
    _hasLoadedTenderInterestSettings = NO
    _isLoadingTenderInterestSettings = NO
    _isMutatingTenderInterestSettings = NO
    // Only the reload with this ID is allowed to update this controller's UI.
    _activeReloadID = 0
    
    
    constructor(view: UIView) {
        super(view)
        
    }
    
    
    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
        RHCore.sharedInstance.offererConfigurationStore.subscribe(this, (settings) => {
            this._applyTenderInterestSettings(settings)
        })
    }
    
    
    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
        RHCore.sharedInstance.offererConfigurationStore.unsubscribe(this)
    }
    
    
    override async viewDidAppear() {
        await super.viewDidAppear()
        await this.reloadTenderInterest()
    }


    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (
            event.name === RHCore.broadcastEventName.activeCompanyDidChange ||
            event.name === RHCore.broadcastEventName.currentCompanyAffiliationDidChange
        ) {
            this.reloadTenderInterest().then(undefined)
        }
    }


    async reloadTenderInterest() {
        const activeReloadID = this._activeReloadID + 1
        this._activeReloadID = activeReloadID
        this._updateTenderInterestLoadingState(YES)
        try {
            const settings = await RHCore.sharedInstance.offererConfigurationStore.retrieveTenderInterestSettings()
            if (this._activeReloadID !== activeReloadID) {
                return
            }
            this._applyTenderInterestSettings(settings)
            this._loadCPVLabelsForRows(activeReloadID)
        }
        catch (error) {
            console.error("Error loading tender interests:", error)
            if (this._activeReloadID === activeReloadID) {
                let alertMessage = "Ettevõtte CPV-valiku laadimine ebaõnnestus."
                if (error instanceof Error && error.message) {
                    alertMessage = error.message
                }
                CBDialogViewShower.alert(alertMessage)
            }
        }
        finally {
            if (this._activeReloadID === activeReloadID) {
                this._updateTenderInterestLoadingState(NO)
            }
        }
    }
    
    
    async _showCompanyTenderInterestDialog() {
        this.editCompanyTenderInterestButton.loading = YES
        try {
            await RHCPVCodeFormatter.autocompleteStrings()
            this.cpvTreeDialog.lockedSelectedCodes =
                this._cpvTreeIdsForCodes(this._companyDefaultTenderInterestCodes)
            this.cpvTreeDialog.configureWithObject({
                dialogTitle: "Täienda ettevõtte CPV-valikut",
                confirmButtonTitle: "Salvesta muudatused",
                cancelButtonTitle: "Tühista",
                allowsMultipleSelection: YES,
                selectionExpansionMode: "selectDescendantsOnAdd"
            })
            await this.cpvTreeDialog.show(
                this._cpvTreeIdsForCodes(this._companyTenderInterestCodes),
                async selectedCodes => {
                    await this._saveCompanyTenderInterestCodes(
                        this._additionalCodesFromCompanySelection(selectedCodes)
                    )
                },
                () => {
                    this.editCompanyTenderInterestButton.focus()
                }
            )
        }
        catch (error) {
            console.error("Error loading CPV picker:", error)
            let alertMessage = "CPV-koodide laadimine ebaõnnestus."
            if (error instanceof Error && error.message) {
                alertMessage = error.message
            }
            CBDialogViewShower.alert(alertMessage)
        }
        finally {
            this.editCompanyTenderInterestButton.loading = NO
        }
    }
    
    
    async _showPersonalTenderInterestDialog() {
        this.editPersonalTenderInterestButton.loading = YES
        try {
            await RHCPVCodeFormatter.autocompleteStrings()
            this.cpvTreeDialog.lockedSelectedCodes = []
            this.cpvTreeDialog.configureWithObject({
                dialogTitle: "Vali täiendavad CPV-koodid",
                confirmButtonTitle: "Salvesta valik",
                cancelButtonTitle: "Tühista",
                allowsMultipleSelection: YES,
                selectionExpansionMode: "selectDescendantsOnAdd"
            })
            await this.cpvTreeDialog.show(
                this._cpvTreeIdsForCodes(this._personalCodesForEditing()),
                async selectedCodes => {
                    await this._savePersonalTenderInterestCodes(selectedCodes)
                },
                () => {
                    this.editPersonalTenderInterestButton.focus()
                }
            )
        }
        catch (error) {
            console.error("Error loading CPV picker:", error)
            let alertMessage = "CPV-koodide laadimine ebaõnnestus."
            if (error instanceof Error && error.message) {
                alertMessage = error.message
            }
            CBDialogViewShower.alert(alertMessage)
        }
        finally {
            this.editPersonalTenderInterestButton.loading = NO
        }
    }
    
    
    async _removeCompanyTenderInterestCode(cpvCode: string) {
        if (this._isMutatingTenderInterestSettings) {
            return
        }
        const nextCodes = this._companyAdditionalTenderInterestCodes.filter(
            existingCode => existingCode !== cpvCode
        )
        await this._saveCompanyTenderInterestCodes(nextCodes)
    }


    async _removePersonalTenderInterestCode(cpvCode: string) {
        if (this._isMutatingTenderInterestSettings) {
            return
        }
        const nextCodes = this._personalCodesForEditing().filter(existingCode => existingCode !== cpvCode)
        await this._savePersonalTenderInterestCodes(nextCodes)
    }


    async _saveCompanyTenderInterestCodes(
        cpvCodes: string[],
        sourceButton: RHButton = this.editCompanyTenderInterestButton
    ) {
        await this._performTenderInterestMutation(
            () => RHCore.sharedInstance.offererConfigurationStore
                .saveCompanyTenderInterestCodes(cpvCodes),
            "Ettevõtte CPV-valiku salvestamine ebaõnnestus.",
            sourceButton
        )
    }


    async _savePersonalTenderInterestCodes(
        cpvCodes: string[],
        sourceButton: RHButton = this.editPersonalTenderInterestButton
    ) {
        await this._performTenderInterestMutation(
            () => RHCore.sharedInstance.offererConfigurationStore
                .savePersonalTenderInterestCodes(cpvCodes),
            "Täiendavate CPV-koodide salvestamine ebaõnnestus.",
            sourceButton
        )
    }


    async _defaultSelectionDidChange() {
        if (this.defaultSelectionCheckbox.selected) {
            this._confirmAndRestoreDefaultSelection()
            return
        }

        if (this._canEditCompanyDefault) {
            this.defaultSelectionCheckbox.selected = this._companyUsesSystemDefault
            return
        }

        await this._performTenderInterestMutation(
            () => RHCore.sharedInstance.offererConfigurationStore
                .saveCompanySelectionInclusionForPersonalNotifications(
                    NO,
                    this._personalCodesForEditing()
                ),
            "Ettevõtte CPV-valiku arvestamise salvestamine ebaõnnestus."
        )
    }


    _confirmAndRestoreDefaultSelection() {
        const shower = CBDialogViewShower.showQuestionDialog(
            this._restoreDefaultsConfirmationQuestion(),
            () => {
                this.defaultSelectionCheckbox.selected =
                    this._isDefaultSelectionEnabled()
            }
        )
        shower.dialogView.view.yesButton.titleLabel.configureWithObject({
            text: "Jah, arvesta"
        })
        if (this._canEditCompanyDefault) {
            shower.dialogView.view.yesButton.titleLabel.configureWithObject({
                text: "Jah, eemalda lisakoodid"
            })
        }
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._restoreDefaultTenderInterestSettings()
        }
    }


    _confirmAndClearAllCompanyTenderInterestCodes() {
        this._confirmAndClearAllTenderInterestCodes(
            "Kas soovid kindlasti kustutada kõik ettevõtte lisatud CPV-koodid? " +
            "Keskkonna vaikekoodid jäävad ettevõtte valikusse.",
            this.clearCompanyTenderInterestButton,
            this.editCompanyTenderInterestButton,
            async () => {
                await this._saveCompanyTenderInterestCodes(
                    [],
                    this.clearCompanyTenderInterestButton
                )
            }
        )
    }


    _confirmAndClearAllPersonalTenderInterestCodes() {
        this._confirmAndClearAllTenderInterestCodes(
            "Kas soovid kindlasti kustutada kõik enda lisatud CPV-koodid? " +
            "Valik „Arvesta ettevõtte CPV-valikut“ ei muutu.",
            this.clearPersonalTenderInterestButton,
            this.editPersonalTenderInterestButton,
            async () => {
                await this._savePersonalTenderInterestCodes(
                    [],
                    this.clearPersonalTenderInterestButton
                )
            }
        )
    }


    _confirmAndClearAllTenderInterestCodes(
        question: string,
        sourceButton: RHButton,
        focusAfterClearButton: RHButton,
        clearOperation: () => Promise<void>
    ) {
        const shower = CBDialogViewShower.showQuestionDialog(
            question,
            () => {
                sourceButton.focus()
            }
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Jah, kustuta"
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await clearOperation()
            focusAfterClearButton.focus()
        }
    }


    _restoreDefaultsConfirmationQuestion(): string {
        if (this._canEditCompanyDefault) {
            return "Kas soovid kindlasti kustutada kõik ettevõtte lisatud CPV-koodid? " +
                "Ettevõtte CPV-valikusse jäävad keskkonna vaikekoodid."
        }
        return "Kas soovid taas arvestada ettevõtte CPV-valikut? " +
            "Sinu lisatud CPV-koodid eemaldatakse."
    }


    async _restoreDefaultTenderInterestSettings() {
        if (this._canEditCompanyDefault) {
            await this._performTenderInterestMutation(
                () => RHCore.sharedInstance.offererConfigurationStore
                    .restoreSystemDefaultCompanyTenderInterestCodes(),
                "Ettevõtte lisatud CPV-koodide eemaldamine ebaõnnestus."
            )
            return
        }

        await this._performTenderInterestMutation(
            () => RHCore.sharedInstance.offererConfigurationStore
                .restoreCompanyDefaultTenderInterestCodesForPersonalNotifications(),
            "Ettevõtte CPV-valiku arvestamise taastamine ebaõnnestus."
        )
    }


    async _performTenderInterestMutation(
        operation: () => Promise<RHOffererTenderInterestSettings>,
        failureMessage: string,
        sourceButton?: RHButton
    ) {
        if (this._isMutatingTenderInterestSettings) {
            return
        }
        this._isMutatingTenderInterestSettings = YES
        if (sourceButton) {
            sourceButton.loading = YES
        }
        this._updateTenderInterestControlAvailability()
        try {
            await operation()
        }
        catch (error) {
            console.error("Error saving tender-interest settings:", error)
            CBDialogViewShower.alert(failureMessage)
            this.defaultSelectionCheckbox.selected =
                this._isDefaultSelectionEnabled()
        }
        finally {
            this._isMutatingTenderInterestSettings = NO
            if (sourceButton) {
                sourceButton.loading = NO
            }
            this._updateTenderInterestControlAvailability()
        }
    }


    _loadCPVLabelsForRows(activeReloadID: number) {
        RHCPVCodeFormatter.autocompleteStrings().then(() => {
            if (this._activeReloadID === activeReloadID) {
                this._updateTenderInterestRows()
            }
        }).catch(error => {
            console.error("Error loading CPV labels:", error)
        })
    }
    
    
    _applyTenderInterestSettings(settings: RHOffererTenderInterestSettings) {
        let companyDefaultCpvCodes = settings.companyDefaultCpvCodes
        let companyAdditionalCpvCodes = settings.companyAdditionalCpvCodes
        if (!companyDefaultCpvCodes || !companyAdditionalCpvCodes) {
            companyDefaultCpvCodes = []
            companyAdditionalCpvCodes = [...settings.companyCpvCodes]
            if (settings.companyUsesSystemDefault) {
                companyDefaultCpvCodes = [...settings.companyCpvCodes]
                companyAdditionalCpvCodes = []
            }
        }
        this._companyTenderInterestCodes = [...settings.companyCpvCodes]
        this._companyDefaultTenderInterestCodes =
            [...companyDefaultCpvCodes]
        this._companyAdditionalTenderInterestCodes =
            [...companyAdditionalCpvCodes]
        this._personalTenderInterestCodes = [...settings.personalCpvCodes]
        this._companyUsesSystemDefault =
            this._companyAdditionalTenderInterestCodes.length === 0
        this._personalIncludesCompanySelection =
            settings.personalIncludesCompanySelection
        this._canEditCompanyDefault = settings.canEditCompanyDefault
        this._hasLoadedTenderInterestSettings = YES
        this.tenderInterestActionRow.configureForCompanyAdministrator(
            this._canEditCompanyDefault
        )
        this._companyTenderInterestActionDescriptor.hidden =
            !this._canEditCompanyDefault ||
            this._companyAdditionalTenderInterestCodes.length === 0
        this.companyTenderInterestDataView.configureWithObject({
            descriptors: this._companyTenderInterestDescriptors,
            titleLabel: { text: "Ettevõtte CPV-valik" },
            noDataMessage: "Ettevõtte CPV-valikus ei ole CPV-koode."
        })
        if (this._canEditCompanyDefault) {
            this.defaultSelectionCheckbox.configureWithObject({
                selected: this._isDefaultSelectionEnabled(),
                titleLabel: { text: "Kasuta ainult keskkonna vaikekoode" }
            })
            this.roleGuidanceView.configureContent(
                "Ettevõtte peakasutaja võimalused",
                administratorTenderInterestExplanation,
                administratorTenderInterestConsequences
            )
            this.cpvSelectionGuidanceView.configureContent(
                "Ettevõtte CPV-valiku täiendamine",
                companyCpvSelectionActionExplanation,
                companyCpvSelectionActionOutcome
            )
        }
        else {
            this.defaultSelectionCheckbox.configureWithObject({
                selected: this._isDefaultSelectionEnabled(),
                titleLabel: { text: "Arvesta ettevõtte CPV-valikut" }
            })
            this.roleGuidanceView.configureContent(
                "Pakkuja ettevõtte esindaja võimalused",
                companyRepresentativeTenderInterestExplanation,
                companyRepresentativeTenderInterestConsequences
            )
            this.cpvSelectionGuidanceView.configureContent(
                "Täiendavate CPV-koodide muutmine",
                personalCpvSelectionActionExplanation,
                personalCpvSelectionActionOutcome
            )
        }
        this._updateTenderInterestRows()
    }


    _isDefaultSelectionEnabled(): boolean {
        if (this._canEditCompanyDefault) {
            return this._companyUsesSystemDefault
        }
        return this._personalIncludesCompanySelection
    }


    _personalCodesForEditing(): string[] {
        return [...this._personalTenderInterestCodes]
    }


    _additionalCodesFromCompanySelection(selectedCodes: string[]): string[] {
        const defaultCodeSet = new Set(
            this._cpvTreeIdsForCodes(this._companyDefaultTenderInterestCodes)
        )
        return selectedCodes.filter(cpvCode => !defaultCodeSet.has(cpvCode))
    }


    _cpvTreeIdsForCodes(cpvCodes: string[]): string[] {
        const treeIds: string[] = []
        for (const cpvCode of cpvCodes) {
            const label = RHCPVCodeFormatter.labelForCPVUsingLoadedData(cpvCode)
            const treeId = RHCPVCodeFormatter.codeFromCPVText(label)
            if (treeId) {
                treeIds.push(treeId)
            }
        }
        return treeIds
    }
    
    
    _updateTenderInterestRows() {
        this.companyTenderInterestDataView.data = this._rowsForCpvCodes(
            this._companyTenderInterestCodes,
            this._companyAdditionalTenderInterestCodes
        )
        this.personalTenderInterestDataView.data = this._rowsForCpvCodes(this._personalTenderInterestCodes)
        this.editCompanyTenderInterestButton.titleLabel.text =
            "Täienda ettevõtte CPV-valikut"
        if (this._personalTenderInterestCodes.length === 0) {
            this.editPersonalTenderInterestButton.titleLabel.text = "Lisa täiendavaid CPV-koode"
        }
        else {
            this.editPersonalTenderInterestButton.titleLabel.text = "Muuda täiendavaid CPV-koode"
        }
        this.tenderInterestActionRow.setNeedsLayout()
        this.tenderInterestActionRow.hidden = NO
        this.defaultSelectionCheckbox.hidden = NO
        this.personalTenderInterestDataView.hidden = this._canEditCompanyDefault
        this._updateTenderInterestControlAvailability()
        this.view.setNeedsLayout()
    }


    _rowsForCpvCodes(
        cpvCodes: string[],
        removableCpvCodes: string[] = cpvCodes
    ): TenderInterestRow[] {
        const rows: TenderInterestRow[] = []
        const removableCpvCodeSet = new Set(removableCpvCodes)
        for (const cpvCode of cpvCodes) {
            rows.push({
                cpvCode,
                cpvText: RHCPVCodeFormatter.labelForCPVUsingLoadedData(cpvCode),
                canRemove: removableCpvCodeSet.has(cpvCode)
            })
        }
        return rows
    }


    _updateTenderInterestLoadingState(loading: boolean) {
        this._isLoadingTenderInterestSettings = loading
        this.companyTenderInterestDataView.loading = loading
        this.personalTenderInterestDataView.loading = loading
        this._updateTenderInterestControlAvailability()
    }


    _updateTenderInterestControlAvailability() {
        let editingEnabled = YES
        if (
            !this._hasLoadedTenderInterestSettings ||
            this._isLoadingTenderInterestSettings ||
            this._isMutatingTenderInterestSettings
        ) {
            editingEnabled = NO
        }

        this.defaultSelectionCheckbox.enabled = editingEnabled
        if (this._canEditCompanyDefault && this._companyUsesSystemDefault) {
            this.defaultSelectionCheckbox.enabled = NO
        }
        this.editCompanyTenderInterestButton.enabled =
            editingEnabled && this._canEditCompanyDefault
        this.clearCompanyTenderInterestButton.enabled =
            editingEnabled &&
            this._canEditCompanyDefault &&
            this._companyAdditionalTenderInterestCodes.length > 0
        this.clearCompanyTenderInterestButton.hidden =
            !this._canEditCompanyDefault ||
            this._companyAdditionalTenderInterestCodes.length === 0
        this.editPersonalTenderInterestButton.enabled =
            editingEnabled && !this._canEditCompanyDefault
        this.clearPersonalTenderInterestButton.enabled =
            editingEnabled &&
            !this._canEditCompanyDefault &&
            this._personalTenderInterestCodes.length > 0
        this.clearPersonalTenderInterestButton.hidden =
            this._canEditCompanyDefault ||
            this._personalTenderInterestCodes.length === 0
        this.companyTenderInterestDataView.setNeedsLayout()
        this.personalTenderInterestDataView.setNeedsLayout()
    }
    
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        
        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            
            .rectangleForNextRow(RHSpacing.medium, this.roleGuidanceView)
            .assignedAsFrameOfView(this.roleGuidanceView)

            .IF(this._canEditCompanyDefault)
            .rectangleForNextRow(RHSpacing.large, this.cpvSelectionGuidanceView)
            .assignedAsFrameOfView(this.cpvSelectionGuidanceView)

            .rectangleForNextRow(RHSpacing.medium, this.tenderInterestActionRow)
            .assignedAsFrameOfView(this.tenderInterestActionRow)

            .rectangleForNextRow(
                RHSpacing.medium,
                RHCompanyTenderInterestViewTokens.dataViewHeights.administratorCompany
            )
            .rectangleWithWidth(bounds.width, 0.5)
            .assignedAsFrameOfView(this.companyTenderInterestDataView)

            .ELSE()
            .rectangleForNextRow(
                RHSpacing.medium,
                RHCompanyTenderInterestViewTokens.dataViewHeights.representativeCompanyDefaults
            )
            .rectangleWithWidth(bounds.width, 0.5)
            .assignedAsFrameOfView(this.companyTenderInterestDataView)

            .rectangleForNextRow(RHSpacing.large, this.cpvSelectionGuidanceView)
            .assignedAsFrameOfView(this.cpvSelectionGuidanceView)

            .rectangleForNextRow(RHSpacing.medium, this.tenderInterestActionRow)
            .assignedAsFrameOfView(this.tenderInterestActionRow)
            
            .rectangleForNextRow(
                RHSpacing.medium,
                RHCompanyTenderInterestViewTokens.dataViewHeights.representativePersonalExtras
            )
            .rectangleWithWidth(bounds.width, 0.5)
            .assignedAsFrameOfView(this.personalTenderInterestDataView)
            .ENDIF()
    }
    
}
